/** Browser-only client for the authenticated, metadata-only LandGate BYOK API. */
import { authedFetch } from '../../lib/authedFetch';
import {
  LANDGATE_CONNECTION_CONTRACT_VERSION,
  isLandGateAuthScheme,
  isLandGateConnectionState,
  type DeleteLandGateConnectionRequest,
  type LandGateConnectionErrorCode,
  type LandGateConnectionErrorResponse,
  type LandGateConnectionMetadata,
  type LandGateConnectionResponse,
  type PutLandGateConnectionRequest,
} from './landGateConnectionTypes';

const CONNECTION_PATH = '/api/landgate/connection';

export const OPEN_LANDGATE_CONNECTION_EVENT = 'open-landgate-connection' as const;
export const LANDGATE_CONNECTION_CHANGED_EVENT = 'landgate-connection-changed' as const;

export function requestLandGateConnectionDialog(): void {
  window.dispatchEvent(new CustomEvent(OPEN_LANDGATE_CONNECTION_EVENT));
}

const KNOWN_ERROR_CODES = new Set<LandGateConnectionErrorCode>([
  'LANDGATE_CONNECTION_REQUIRED',
  'LANDGATE_CONNECTION_CONFLICT',
  'LANDGATE_CONNECTION_INVALID',
  'LANDGATE_CONNECTION_UNAVAILABLE',
  'LANDGATE_CREDENTIAL_REJECTED',
  'LANDGATE_ENDPOINT_REJECTED',
  'LANDGATE_RATE_LIMITED',
  'LANDGATE_REQUEST_TIMEOUT',
  'LANDGATE_INVALID_RESPONSE',
  'LANDGATE_SITE_BOUNDARY_REQUIRED',
  'LANDGATE_UPSTREAM_UNAVAILABLE',
  'AUTH_REQUIRED',
  'FORBIDDEN',
  'UNKNOWN',
]);

export class LandGateConnectionError extends Error {
  readonly code: LandGateConnectionErrorCode;
  readonly status: number;
  readonly retryable: boolean;
  readonly requestId: string;

  constructor(params: {
    message: string;
    code?: LandGateConnectionErrorCode;
    status?: number;
    retryable?: boolean;
    requestId?: string;
  }) {
    super(params.message);
    this.name = 'LandGateConnectionError';
    this.code = params.code ?? 'UNKNOWN';
    this.status = params.status ?? 0;
    this.retryable = params.retryable ?? false;
    this.requestId = params.requestId ?? '';
  }
}

function errorCode(value: unknown): LandGateConnectionErrorCode {
  return typeof value === 'string' && KNOWN_ERROR_CODES.has(value as LandGateConnectionErrorCode)
    ? value as LandGateConnectionErrorCode
    : 'UNKNOWN';
}

function safeErrorMessage(code: LandGateConnectionErrorCode, status: number): string {
  if (code === 'LANDGATE_CONNECTION_REQUIRED' || status === 428) {
    return 'Connect your LandGate account before importing layers.';
  }
  if (code === 'LANDGATE_CONNECTION_CONFLICT' || status === 409) {
    return 'This connection changed in another session. Reload its status and try again.';
  }
  if (code === 'LANDGATE_CREDENTIAL_REJECTED') {
    return 'LandGate rejected this credential. Replace it with an active API key.';
  }
  if (code === 'LANDGATE_ENDPOINT_REJECTED') {
    return 'LandGate rejected this endpoint. Use the official HTTPS endpoint from your agreement.';
  }
  if (code === 'LANDGATE_RATE_LIMITED') return 'LandGate is limiting requests. Wait before retrying the import.';
  if (code === 'LANDGATE_REQUEST_TIMEOUT') return 'LandGate did not respond in time. Retry the import.';
  if (code === 'LANDGATE_INVALID_RESPONSE') return 'LandGate returned data that could not be imported safely.';
  if (code === 'LANDGATE_SITE_BOUNDARY_REQUIRED') return 'Add a parcel boundary before importing LandGate layers.';
  if (code === 'AUTH_REQUIRED' || status === 401) return 'Sign in to manage your LandGate connection.';
  if (code === 'FORBIDDEN' || status === 403) return 'You do not have access to this LandGate connection.';
  if (code === 'LANDGATE_CONNECTION_UNAVAILABLE') return 'LandGate connections are unavailable in this deployment.';
  if (code === 'LANDGATE_UPSTREAM_UNAVAILABLE') return 'LandGate could not be reached. Try again when the service is available.';
  if (status >= 500) return 'The LandGate connection service is temporarily unavailable.';
  return 'The LandGate connection could not be updated.';
}

/** Parse structured errors without surfacing upstream/vendor response text. */
export async function landGateConnectionErrorFromResponse(
  response: Response,
): Promise<LandGateConnectionError> {
  let body: Partial<LandGateConnectionErrorResponse> | undefined;
  try {
    body = await response.clone().json() as Partial<LandGateConnectionErrorResponse>;
  } catch {
    body = undefined;
  }
  const detail = body?.error;
  const code = errorCode(detail?.code);
  return new LandGateConnectionError({
    code,
    status: response.status,
    retryable: detail?.retryable === true,
    requestId: typeof detail?.requestId === 'string' ? detail.requestId : '',
    // Render only client-owned copy. Even a malformed server implementation
    // must not pass an upstream/vendor response body into the UI.
    message: safeErrorMessage(code, response.status),
  });
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

const FORBIDDEN_METADATA_FIELDS = new Set([
  'apikey',
  'authorization',
  'ciphertext',
  'ciphertextbase64',
  'credential',
  'credentialpayload',
  'endpointurl',
  'kmskeyversionname',
  'plaintext',
  'secret',
  'token',
]);

function containsSecretBearingMetadata(
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>(),
): boolean {
  if (!value || typeof value !== 'object' || depth > 4) return false;
  if (seen.has(value)) return false;
  seen.add(value);
  for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
    const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (FORBIDDEN_METADATA_FIELDS.has(normalizedKey)) return true;
    if (containsSecretBearingMetadata(nested, depth + 1, seen)) return true;
  }
  return false;
}

function invalidMetadata(message = 'The LandGate connection service returned an unsupported response.'): never {
  throw new LandGateConnectionError({
    code: 'LANDGATE_CONNECTION_INVALID',
    message,
  });
}

function isIsoTimestamp(value: string | null): value is string {
  if (!value) return false;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString() === value;
}

function isOfficialLandGateHost(value: string | null): value is string {
  if (!value || value !== value.toLowerCase()) return false;
  return value === 'landgate.com' || value.endsWith('.landgate.com');
}

function parseMetadata(value: unknown): LandGateConnectionMetadata {
  if (!value || typeof value !== 'object') {
    throw new LandGateConnectionError({
      code: 'LANDGATE_CONNECTION_INVALID',
      message: 'The LandGate connection service returned an invalid response.',
    });
  }
  const candidate = value as Record<string, unknown>;
  if (containsSecretBearingMetadata(candidate)) {
    invalidMetadata('The LandGate connection service returned credential data that cannot be displayed safely.');
  }
  if (
    candidate.contractVersion !== LANDGATE_CONNECTION_CONTRACT_VERSION
    || !isLandGateConnectionState(candidate.state)
    || !Number.isSafeInteger(candidate.revision)
    || Number(candidate.revision) < 0
  ) {
    invalidMetadata();
  }
  const authScheme = candidate.authScheme == null
    ? null
    : isLandGateAuthScheme(candidate.authScheme)
      ? candidate.authScheme
      : undefined;
  if (authScheme === undefined) {
    invalidMetadata('The LandGate connection service returned an unsupported authentication scheme.');
  }
  const connectionId = optionalString(candidate.connectionId);
  const credentialVersion = Number.isSafeInteger(candidate.credentialVersion)
    ? Number(candidate.credentialVersion)
    : null;
  const endpointHost = optionalString(candidate.endpointHost);
  const savedAt = optionalString(candidate.savedAt);
  const lastVerifiedAt = optionalString(candidate.lastVerifiedAt);
  const parsedFailureCode = candidate.lastFailureCode == null
    ? undefined
    : errorCode(candidate.lastFailureCode);
  if (candidate.lastFailureCode != null && parsedFailureCode === 'UNKNOWN') {
    invalidMetadata('The LandGate connection service returned an unsupported failure state.');
  }

  if (candidate.state === 'not-configured') {
    if (
      connectionId !== null
      || credentialVersion !== null
      || endpointHost !== null
      || authScheme !== null
      || candidate.licenceSharingAttested !== false
      || savedAt !== null
      || lastVerifiedAt !== null
      || parsedFailureCode !== undefined
    ) {
      invalidMetadata('The LandGate connection service returned unsafe disconnected metadata.');
    }
  } else {
    if (
      connectionId === null
      || credentialVersion === null
      || credentialVersion < 1
      || !isOfficialLandGateHost(endpointHost)
      || authScheme === null
      || candidate.licenceSharingAttested !== true
      || !isIsoTimestamp(savedAt)
      || (lastVerifiedAt !== null && !isIsoTimestamp(lastVerifiedAt))
    ) {
      invalidMetadata('The LandGate connection service returned incomplete active metadata.');
    }
    if (candidate.state === 'verified' && !isIsoTimestamp(lastVerifiedAt)) {
      invalidMetadata('The LandGate connection service returned an unverified active state.');
    }
    if (candidate.state === 'needs-attention' && parsedFailureCode === undefined) {
      invalidMetadata('The LandGate connection service returned an unclassified attention state.');
    }
    if (candidate.state !== 'needs-attention' && parsedFailureCode !== undefined) {
      invalidMetadata('The LandGate connection service returned a contradictory failure state.');
    }
  }

  return {
    contractVersion: LANDGATE_CONNECTION_CONTRACT_VERSION,
    state: candidate.state,
    revision: Number(candidate.revision),
    connectionId,
    credentialVersion,
    endpointHost,
    authScheme,
    licenceSharingAttested: candidate.licenceSharingAttested === true,
    savedAt,
    lastVerifiedAt,
    ...(parsedFailureCode ? { lastFailureCode: parsedFailureCode } : {}),
  };
}

async function readConnection(response: Response): Promise<LandGateConnectionMetadata> {
  if (!response.ok) throw await landGateConnectionErrorFromResponse(response);
  let body: Partial<LandGateConnectionResponse>;
  try {
    body = await response.json() as Partial<LandGateConnectionResponse>;
  } catch {
    throw new LandGateConnectionError({
      code: 'LANDGATE_CONNECTION_INVALID',
      status: response.status,
      message: 'The LandGate connection service returned an unreadable response.',
    });
  }
  return parseMetadata(body.connection);
}

export function normalizeLandGateEndpoint(input: string): string {
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    throw new LandGateConnectionError({
      code: 'LANDGATE_ENDPOINT_REJECTED',
      message: 'Enter the complete HTTPS endpoint supplied by LandGate.',
    });
  }
  if (
    url.protocol !== 'https:'
    || !url.hostname
    || (url.port !== '' && url.port !== '443')
    || url.username
    || url.password
    || url.search
    || url.hash
    || (url.hostname !== 'landgate.com' && !url.hostname.endsWith('.landgate.com'))
  ) {
    throw new LandGateConnectionError({
      code: 'LANDGATE_ENDPOINT_REJECTED',
      message: 'Use an official landgate.com HTTPS endpoint without credentials, query parameters, or a fragment.',
    });
  }
  return url.toString();
}

export async function getLandGateConnection(
  signal?: AbortSignal,
): Promise<LandGateConnectionMetadata> {
  return readConnection(await authedFetch(CONNECTION_PATH, { method: 'GET', signal }));
}

export async function putLandGateConnection(
  input: Omit<PutLandGateConnectionRequest, 'contractVersion' | 'endpointUrl'> & { endpointUrl: string },
  signal?: AbortSignal,
): Promise<LandGateConnectionMetadata> {
  const request: PutLandGateConnectionRequest = {
    ...input,
    contractVersion: LANDGATE_CONNECTION_CONTRACT_VERSION,
    endpointUrl: normalizeLandGateEndpoint(input.endpointUrl),
  };
  return readConnection(await authedFetch(CONNECTION_PATH, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
    signal,
  }));
}

export async function deleteLandGateConnection(
  expectedRevision: number,
  signal?: AbortSignal,
): Promise<LandGateConnectionMetadata> {
  const request: DeleteLandGateConnectionRequest = {
    contractVersion: LANDGATE_CONNECTION_CONTRACT_VERSION,
    expectedRevision,
  };
  return readConnection(await authedFetch(CONNECTION_PATH, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
    signal,
  }));
}
